import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
   useColorModeValue,
   Card,
   Grid,
   GridItem,
   Button,
   CardBody,
   CardHeader,
   Heading,
   useToast,
   CardFooter,
   Flex,
   useDisclosure,
   Image,
   Box,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   FormControl,
   FormLabel,
   Input,
   Table,
   Thead,
   Tbody,
   Tr,
   Th,
   Td,
   TableContainer,
   IconButton,
   Switch,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { getDaybook, getDaybookById } from '../../../../../redux/actions/daybook';
import { renderFormControl, renderSelectFormControl } from 'utils/DynamicFormData';
import * as Yup from 'yup';
import LineItemsTable from '../../../sales/Pages/SalesInvoiceMaster/LineItemsTable';
import { getBillTerm } from '../../../../../redux/actions/billTerm';
import BillTerms from '../../../sales/Pages/SalesInvoiceMaster/BillTerms';
// import { getCompanies } from '../../../../../redux/actions/company';
import { getItem } from '../../../../../redux/actions/item';
import { getAccount, addAccount, getAccountById } from '../../../../../redux/actions/account';
import { useNavigate, useParams } from 'react-router-dom';
import {
   deletePurchaseLineItem,
   addPurchaseInvoice,
   updatePurchaseInvoice,
   getPurchaseInvoice,
   getPurchaseInvoices,
} from '../../../../../redux/actions/purchaseInvoice';
import DeleteDialog from '../../../masters/components/DeleteDialog';
import { FaPlus } from 'react-icons/fa';
import DaybookForm from '../../../masters/Pages/DaybookMaster/DaybookForm';
import AddEditForm from '../../../masters/Pages/CustomerMaster/AddEditForm';
import { usePurchaseDaybook } from '../../../../../contexts';
import TaxableValue from '../../../sales/Pages/SalesInvoiceMaster/TaxableValue';
import phone from 'assets/img/nfts/phone.svg';
import Email from 'assets/img/nfts/email.svg';
import map from 'assets/img/nfts/map.svg';
import { getMainAndSubHsn } from '../../../../../redux/actions/mainAndSubHsn';
import useCalculateTaxAmount from 'hooks/useSalesTaxAmount';
import { SupplyTypes } from 'utils/dropdownEnum';
// import { ACCOUNT_TYPE } from '../../../../../constants/index';
// import { deletePurchaseLineDetails } from '../../../../../redux/actions/purchaseLineDetail';
import GstDropDown from 'components/GSTDropDown/GstDropDown';
import { getStock } from '../../../../../redux/actions/salesLineDetail';
import { getDefaultId, getAccountAddress, getGstTaxType, getGstSubTaxType } from 'utils/util';
import { getWarehouse } from '../../../../../redux/actions/warehouse';
import { FaTrash } from 'react-icons/fa';
import { useUser } from '../../../../../contexts/UserContext';
import { removeDuplicates } from '../../../../../utils/util';
import { useGlobal } from '../../../../../contexts';

const validationSchema = Yup.object().shape({
   header: Yup.object().shape({
      daybookId: Yup.number().required('Daybook is required').positive('Invalid daybook selected'),

      docno: Yup.string().required('Document number is required').trim(),
      partyNo: Yup.string().required('Party number is required').trim(),
      partyDate: Yup.date().required('Party date is required').max(new Date(), 'Party date cannot be in the future'),
      transactionAt: Yup.date()
         .required('Transaction date is required'),

      accountId: Yup.number().required('Account is required').positive('Invalid account selected'),

      basicAmount: Yup.number()
         .required('Basic amount is required')
         .min(0, 'Basic amount cannot be negative')
         .test(
            'is-decimal',
            'Amount cannot have more than 2 decimal places',
            (value) => !value || Number.isInteger(value * 100),
         ),

      netAmount: Yup.number().required('Net amount is required'),

      // billtoStateId: Yup.number()
      //   .required('Billing state is required')
      //   .positive('Invalid billing state'),

      // shiptoStateId: Yup.number()
      //   .required('Shipping state is required')
      //   .positive('Invalid shipping state'),
   }),

   lineItem: Yup.array()
      .of(
         Yup.object().shape({
            itemRate: Yup.number()
               .required('Item rate is required')
               .min(0, 'Item rate cannot be negative')
               .test(
                  'is-decimal',
                  'Rate cannot have more than 2 decimal places',
                  (value) => !value || Number.isInteger(value * 100),
               ),

            qty1: Yup.number().required('Quantity is required').min(0, 'Quantity cannot be negative'),

            qty2: Yup.number().default(0).min(0, 'Quantity cannot be negative'),

            qty3: Yup.number().default(0).min(0, 'Quantity cannot be negative'),

            amount: Yup.number().required('Amount is required').min(0, 'Amount cannot be negative'),

            ratePer: Yup.number().min(0, 'Rate per cannot be negative'),

            uomNoForRate: Yup.number()
               .integer('UOM number must be a whole number')
               .min(0, 'UOM number cannot be negative'),

            salesprice2: Yup.number().default(0).min(0, 'Sales price cannot be negative'),

            salesprice3: Yup.number().default(0).min(0, 'Sales price cannot be negative'),

            lineNumber: Yup.number()
               .required('Line number is required')
               .positive('Line number must be positive')
               .integer('Line number must be a whole number'),
         }),
      )
      .min(1, 'At least one line item is required'),
});

const PurchaseInvoiceMasterForm = () => {
   const toast = useToast();
   const dispatch = useDispatch();
   const { companyList } = useGlobal();
   const [billTerms, setBillTerms] = useState([]);
   const [isSameState, setIsSameState] = useState(null);
   const [dueDays, setDueDays] = useState(0);
   const [dueAt, setDueAt] = useState('');
   const textColor = useColorModeValue('gray.800', 'white');
   const [errors, setErrors] = useState({});
   const [showGSTColumn, setShowGSTColumn] = useState(false);
   const [isDeleteDialogOpen, setDeleteDialogOpen] = React.useState(false); // State to control the delete dialog
   const [deleteLineItemId, setDeleteLineItemId] = useState(null);
   // const compId = localStorage.getItem('companyId');
   const daybookId = localStorage.getItem('daybook');
   const accountId = localStorage.getItem('accountId');
   const itemData = useSelector((state) => state.item?.list);
   const daybookData = useSelector((state) => state.daybook?.list || []);
   const account = useSelector((state) => state?.account?.list);
   const selectAccount = useSelector((state) => state?.account?.single);
   const billTermData = useSelector((state) => state.billTerm?.list);
   const salesInvoice = useSelector((state) => state.purchaseInvoice?.single);
   const warehouse = useSelector((state) => state?.warehouse?.list);
   // const hsnData = useSelector((state) => state.mainAndSubHsn?.list);
   // const companyData = useSelector((state) => state?.company?.list || []);
   const stocks = useSelector((state) => state?.salesLineDetail?.stocks || []);
   const glgroup = useSelector((state) => state.glGroup.list || []);

   const selectedDaybook = useSelector((state) => state.daybook.single);

   const targetGlGroup = glgroup.find((group) => group.code === 'E');
   const [selectedItemParent, setSelectedItemParent] = useState({
      hsnData: null,
   });
   const [rows, setRows] = useState([
      { serialNo: '', serialNo2: '', barcode: '', note1: '', isOld: selectedDaybook?.isOld },
   ]);
   const [totalTaxableValue, setTotalTaxableValue] = useState('0.00');
   const [totalGSTValues, setTotalGSTValues] = useState({
      gstAmount: '0.00',
      cgstAmount: '0.00',
      sgstAmount: '0.00',
      igstAmount: '0.00',
      utgstAmount: '0.00',
   });
   const { isOpen: isOpenModal, onOpen: onOpenModal, onClose: onCloseModal } = useDisclosure();

   const navigate = useNavigate();
   const { id } = useParams();
   const { user } = useUser();
   // const [companyId, setCompanyId] = useState(Number(user?.loginhistory?.companyId));

   const { lastPurchaseDaybook, setDaybookId } = usePurchaseDaybook();

   const { isOpen, onOpen, onClose } = useDisclosure();
   const { isOpen: isAccountOpen, onOpen: onAccountOpen, onClose: onAccountClose } = useDisclosure();
   const [isEditMode, setIsEditMode] = React.useState(false);
   const [currentRow, setCurrentRow] = React.useState(null);
   const [data, setData] = React.useState([...daybookData]);
   const [amounts, setAmounts] = useState({});
   const [selectedAccount, setSelectedAccount] = useState(null);
   const [selectedStocks, setSelectedStocks] = useState([]);
   const [lastItemId, setLastItemId] = useState(null);
   const [currentIndex, setCurrentIndex] = useState(null);
   const [stockList, setStockList] = useState(stocks);
   const [hasFetchedAccount, setHasFetchedAccount] = useState(false);

   useEffect(() => {
      if (id) {
         dispatch(getPurchaseInvoice(id));
      }
   }, [dispatch, id]);

   useEffect(() => {
      if (accountId) {
         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               accountId: accountId,
            },
         }));
      }
   }, [accountId]);

   // Function to update the total amount
   const updateTotalAmount = (newTotal) => {
      setTotalAmount(newTotal);
   };

   const initialLineItem = {
      typeOfSupply: SupplyTypes.find((item) => item.id === lastPurchaseDaybook?.typeOfSupply)?.name || 'Goods',
      headerId: 1,
      gst: 0,
      netGst: 0,
      gstAmount: 0,
      itemId: 0,
      lineNumber: 1,
      itemRate: 0,
      amount: 0,
      netAmount: 0,
      taxableAmount: 0,
      ratePer: 0,
      uomNoForRate: 0,
      salesprice1: 0,
      salesprice2: 0,
      salesprice3: 0,
      qty1: 0,
      qty2: 0,
      qty3: 0,
      hsnData: companyList.find((company) => company.id.toString() === user?.loginhistory?.companyId.toString())
         ?.hsndata,
      sacData: companyList.find((company) => company.id.toString() === user?.loginhistory?.companyId.toString())
         ?.sacdata,
      ratio: null,
      cgstAmount: '0.00',
      sgstAmount: '0.00',
      igstAmount: '0.00',
      utgstAmount: '0.00',

      addLess1Percent: 0,
      addLess1Amount: 0,
      addLess2Percent: 0,
      addLess2Amount: 0,
      addLess3Percent: 0,
      addLess3Amount: 0,

      stock: [],
   };

   const [row, setRow] = useState({
      header: {
         daybookId: lastPurchaseDaybook?.daybookId || '',
         docno: '',
         transactionAt: new Date().toISOString().split('T')[0],
         companyId: '',
         accountId: '',
         basicAmount: 0,
         additionAmount: '',
         deductionAmount: '',
         netAmount: 0,
         dueDays: '',
         dueAt: '',
         billtoStateId: 618,
         shiptoStateId: 2,
         partyNo: '',
         partyDate: new Date().toISOString().split('T')[0],
      },
      lineItem: [initialLineItem],
   });

   const clearFormData = () => {
      setRow({
         header: {
            daybookId: '',
            docno: '',
            transactionAt: new Date().toISOString().split('T')[0],
            companyId: '',
            accountId: '',
            basicAmount: 0,
            additionAmount: '',
            deductionAmount: '',
            netAmount: 0,
            dueDays: '',
            dueAt: '',
            billtoStateId: '',
            shiptoStateId: '',
            partyNo: '',
            partyDate: new Date().toISOString().split('T')[0],
         },
         lineItem: [initialLineItem],
      });
      setErrors({});
      setSelectedStocks([]);
      setCurrentIndex(null);
   };

   const [totalAmount, setTotalAmount] = useState(0);

   const {
      totals,
      handleQtyChange,
      handleRateChange,
      handleDiscountChange,
      setTotalBillTermAmount,
      setBillTerm,
      finalNetAmount,
   } = useCalculateTaxAmount({
      row,
      setRow,
      setBillTerms,
   });

   // const getAmount = useCallback(() => {
   //   const basicAmount = row.lineItem.reduce((acc, item) => {
   //     const lineTotal =
   //       (parseFloat(item.salesprice1) || 0) * (parseFloat(item.qty1) || 0); // Use parseFloat and fallback to 0 if invalid
   //     return acc + lineTotal;
   //   }, 0);
   //   let additionAmount = 0;
   //   let deductionAmount = 0;
   //   billTerms?.forEach((term) => {
   //     const amount = parseFloat(term.amount) || 0;
   //     const termData = billTermData.find(
   //       (billTerm) => term.billTermId === billTerm.id,
   //     );

   //     if (termData?.signname === 'Subtraction') {
   //       deductionAmount += Math.abs(amount);
   //     } else if (termData?.signname === 'Addition') {
   //       additionAmount += Math.abs(amount);
   //     } else if (termData?.signname === 'Both') {
   //       if (amount < 0) {
   //         deductionAmount += Math.abs(amount);
   //       } else {
   //         additionAmount += amount;
   //       }
   //     }
   //   });

   //   let total = totals.netAmount + additionAmount;
   //   total = total - deductionAmount;

   //   let netTotal = totals.netAmount + additionAmount;
   //   netTotal = netTotal - deductionAmount;

   //   let billTerm = additionAmount - deductionAmount;
   //   setBillTerm(billTerm);
   //   setTotalBillTermAmount(netTotal);

   //   setTotalTaxableValue((prev) => {
   //     let total = Number(prev) + additionAmount;
   //     total = total - deductionAmount;
   //     return parseFloat(total).toFixed(2);
   //   });

   //   const netAmount = total;
   //   setRow((prev) => ({ ...prev, netAmount }));
   //   setAmounts({
   //     basicAmount,
   //     additionAmount,
   //     deductionAmount,
   //     netAmount,
   //   });
   // }, [billTermData, billTerms, row.lineItem]);

   useEffect(() => {
      // dispatch(getAccount({ type: ACCOUNT_TYPE.VENDOR }));
      dispatch(getAccount({ compId: user?.loginhistory?.companyId, status: true }));
      dispatch(getDaybook({ type: 11, companyId: Number(user?.loginhistory?.companyId) }));
      // dispatch(getCompanies());
      dispatch(getItem({ companyId: user.loginhistory.companyId }));
      dispatch(getMainAndSubHsn());
      dispatch(getWarehouse());
   }, [dispatch, user?.loginhistory?.companyId]);

   useEffect(() => {
      if (row?.header?.daybookId || (lastPurchaseDaybook && lastPurchaseDaybook?.daybookId)) {
         dispatch(
            getBillTerm({
               daybookId: row?.header?.daybookId
                  ? row.header.daybookId
                  : lastPurchaseDaybook && lastPurchaseDaybook?.daybookId
                     ? lastPurchaseDaybook?.daybookId
                     : 0,
            }),
         );
         const selectedDaybook = daybookData.find((daybook) => daybook.id === Number(row.header.daybookId));
         setShowGSTColumn(selectedDaybook?.applyGst || false);
      }
   }, [dispatch, lastPurchaseDaybook, row.header.daybookId, daybookData]);

   // useEffect(() => {
   //   getAmount();
   // }, [getAmount]);

   const handleEditValues = useCallback(async () => {
      if (id && salesInvoice) {
         if (salesInvoice?.accountId && !hasFetchedAccount) {
            const accountbyid = {
               id: Number(salesInvoice?.accountId), // id must be number
               compId: String(user?.loginhistory?.companyId), // compId must be string
            };
            dispatch(getAccountById(accountbyid));
            setHasFetchedAccount(true);
         }

         setRow({
            header: {
               daybookId: row.header.daybookId ? row.header.daybookId : salesInvoice.daybookId,
               docno: salesInvoice.docno,
               transactionAt: new Date(salesInvoice.transactionAt).toISOString().split('T')[0],
               dueAt: new Date(salesInvoice.dueAt).toISOString().split('T')[0],
               dueDays: salesInvoice.dueDays,
               accountId: salesInvoice.accountId,
               basicAmount: salesInvoice.basicAmount,
               netAmount: salesInvoice.netAmount,
               partyNo: salesInvoice.partyNo,
               partyDate: new Date(salesInvoice.partyDate).toISOString().split('T')[0],
            },
            lineItem: salesInvoice?.lineitems.map((item) => ({
               ...item,
               itemRate: item.itemRate,
               salesprice1: item.itemRate || 0,
               gst: item.gstPercent || 0,
               // gstAmount: Number(item.netGst) + Number(item.taxableAmount),
               hsnData: item.hsnData
                  ? item.hsnData
                  : companyList.find((company) => company.id.toString() === user?.loginhistory?.companyId.toString())
                     ?.hsndata,
               sacData: item.sacData
                  ? item.sacData
                  : companyList.find((company) => company.id.toString() === user?.loginhistory?.companyId.toString())
                     ?.sacdata,

               stock: item.purchaseLineDetail && item.purchaseLineDetail?.length > 0 ? item.purchaseLineDetail : [],
            })),
         });
         const initialBillTerms = billTermData.map((term) => {
            const existingTerm = salesInvoice.purchasebillterms?.find((billTerm) => billTerm.billTermId === term.id);
            return existingTerm
               ? {
                  // ...existingTerm,
                  id: existingTerm.id,
                  billTermId: term.id,
                  percentage: existingTerm.percentage || 0,
                  amount: existingTerm.amount || 0,
               }
               : { billTermId: term.id, percentage: 0, amount: 0 };
         });
         setBillTerms(initialBillTerms);
      }
   }, [billTermData, id, row.header.daybookId, salesInvoice, user?.loginhistory?.companyId]);

   /// billTermData, id, row.header.daybookId, salesInvoice, user?.loginhistory?.companyId

   useEffect(() => {
      handleEditValues();
   }, [handleEditValues]);

   useEffect(() => {
      if (lastPurchaseDaybook && lastPurchaseDaybook?.docno) {
         // dispatch(getAccount({ type: ACCOUNT_TYPE.VENDOR }));
         dispatch(getAccount({ compId: user?.loginhistory?.companyId, status: true }));
         // Make sure we're working with a string and handle potential NaN cases
         const currentDocNo = String(lastPurchaseDaybook.docno || '0');
         const nextDocNo = isNaN(parseInt(currentDocNo)) ? '1' : String(parseInt(currentDocNo) + 1);

         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               daybookId: lastPurchaseDaybook.daybookId,
               docno: nextDocNo,
            },
            lineItem: prev.lineItem.map((item) => ({
               ...item,
               typeOfSupply: SupplyTypes.find((item) => item.id === lastPurchaseDaybook?.typeOfSupply)?.name,
            })),
         }));
      } else {
         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               docno: '1',
            },
            lineItem: prev.lineItem.map((item) => ({
               ...item,
               typeOfSupply: SupplyTypes.find((item) => item.id === lastPurchaseDaybook?.typeOfSupply)?.name,
            })),
         }));
      }
   }, [dispatch, lastPurchaseDaybook]);

   useEffect(() => {
      if (row.header.transactionAt && dueDays !== undefined) {
         const transactionDate = new Date(row.header.transactionAt);
         const newDueAt = new Date(transactionDate);
         newDueAt.setDate(transactionDate.getDate() + parseInt(dueDays));
         const calculatedDueAt = newDueAt.toISOString().split('T')[0];
         setDueAt(calculatedDueAt);
         handleInputChange('header', 'dueAt', calculatedDueAt);
      }
   }, [dueDays, row.header.transactionAt]);

   useEffect(() => {
      if (daybookId) {
         setDaybookId(daybookId);
      }
   }, [daybookId, setDaybookId]);

   const handleItemSelection = useCallback(
      (value, index) => {
         const selectedItem = itemData?.find((item) => item.id.toString() === value.toString());

         setRow((prev) => ({
            ...prev,
            lineItem: prev.lineItem.map((item, i) =>
               i === index
                  ? {
                     ...item,
                     salesprice1: selectedItem.salesprice1,
                     itemRate: selectedItem.salesprice1,
                     hsnData: selectedItem?.hsnData,
                     sacData: selectedItem?.sacData,
                  }
                  : item,
            ),
         }));
      },
      [itemData],
   );

   const handleTypeOfSupplyChange = useCallback((value, index) => {
      setRow((prev) => {
         return {
            ...prev,
            lineItem: prev.lineItem.map((a, idx) => {
               const sunHsn = a?.hsnData?.subHsn?.find(
                  (hsn) => a.salesprice1 >= hsn.fromRate && a.salesprice1 <= hsn.toRate,
               );
               const taxRate = parseFloat(sunHsn?.gst) / 100;
               return idx === index
                  ? {
                     ...a,
                     gst: sunHsn?.gst ?? 0,
                     gstAmount: a.salesprice1 * a.qty1 * taxRate,
                     ratio: sunHsn?.ratio ?? null,
                  }
                  : a;
            }),
         };
      });
   }, []);

   const handleStockModal = useCallback(
      (itemId, index) => {
         const selectedItem = itemData.find((item) => item.id.toString() === itemId.toString());

         if (selectedItem && selectedItem.itemdetailtype === 1) {
            const stockData = row.lineItem[index]?.stock;
            const rData =
               stockData && stockData?.length > 0
                  ? stockData
                  : [{ serialNo: '', serialNo2: '', barcode: '', note1: '', isOld: selectedDaybook?.isOld }];
            setRows(rData);
            setCurrentIndex(index);
            onOpenModal();
         }
      },
      [itemData, onOpenModal, row.lineItem],
   );

   const handleStockModalOnClick = useCallback(
      async (index) => {
         const itemData = row.lineItem[Number(index)];
         await dispatch(
            getStock({
               itemId: Number(itemData.itemId),
               companyId: Number(user?.loginhistory?.companyId),
               type: "purchase",
            }),
         );
         setLastItemId(itemData.itemId);
         setStockList((prev) => prev.concat(itemData.stock));
         setRows(
            itemData?.stock.map((item) => ({
               note1: item.note1,
               serialNo: item.serialNo,
               serialNo2: item.serialNo2,
               barcode: item.barcode,
               isOld: item.isOld,
               itemId: itemData.itemId,
            })),
         );
         setCurrentIndex(index);
         onOpenModal();
      },
      [dispatch, onOpenModal, row.lineItem, user?.loginhistory?.companyId],
   );

   // Update useEffect to handle bill terms when daybook changes
   useEffect(() => {
      if (billTermData && billTermData?.length > 0 && !id) {
         const initialBillTerms = billTermData.map((term) => ({
            billTermId: term.id,
            percentage: term.percentValue || 0,
            amount: 0,
         }));
         setBillTerms(initialBillTerms);
      }
   }, [billTermData, id]);

   // Add handler for bill term changes
   const handleBillTermChange = (index, values) => {
      setBillTerms((prev) => {
         const newBillTerms = [...prev];
         newBillTerms[index] = {
            ...newBillTerms[index],
            ...values,
         };

         // Get the current basic amount
         const currentBasicAmount = parseFloat(row.header.basicAmount) || 0;
         // const currentBasicAmount = parseFloat(amounts?.basicAmount) || 0;

         // Calculate addition and deduction totals based on the basic amount
         let additionTotal = 0;
         let deductionTotal = 0;

         newBillTerms.forEach((term, idx) => {
            const amount = parseFloat(term.amount) || 0;
            const termData = billTermData[idx];

            if (termData?.signname === 'Subtraction') {
               deductionTotal += Math.abs(amount);
            } else if (termData?.signname === 'Addition') {
               additionTotal += Math.abs(amount);
            } else if (termData?.signname === 'Both') {
               if (amount < 0) {
                  deductionTotal += Math.abs(amount);
               } else {
                  additionTotal += amount;
               }
            }
         });

         // Calculate net amount based on the basic amount
         const netAmount = currentBasicAmount + additionTotal - deductionTotal;

         let billTerm = additionTotal - deductionTotal;
         let netTotal = totals.netAmount + additionTotal;
         netTotal = netTotal - deductionTotal;
         setBillTerm(billTerm);
         setTotalBillTermAmount(netTotal);

         // Update the header amounts
         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               additionAmount: additionTotal.toFixed(2),
               deductionAmount: deductionTotal.toFixed(2),
               netAmount: netAmount.toFixed(2),
               basicAmount: currentBasicAmount.toFixed(2),
            },
         }));

         return newBillTerms;
      });
   };

   const handleInputChange = useCallback(
      (section, field, value, index = null) => {
         if (section === 'header' && field === 'daybookId') {
            dispatch(getBillTerm({ daybookId: value }));
            // Dispatch action to fetch last bill number when daybook is selected
            setDaybookId(Number(value));
            setRow((prev) => ({
               ...prev,
               header: {
                  ...prev.header,
                  daybookId: Number(value),
               },
            }));
         }

         if (section === 'header' && field === 'accountId') {
            const accountbyid = {
               id: Number(value), // id must be number
               compId: String(user?.loginhistory?.companyId), // compId must be string
            };
            dispatch(getAccountById(accountbyid));
         }

         if (section === 'lineItem' && index !== null) {
            setRow((prev) => {
               const updatedLineItems = [...prev.lineItem];
               updatedLineItems[index][field] = Number(value || 0);

               if (['qty1', 'qty2', 'qty3'].includes(field)) handleQtyChange(field, value, index);
               if (['itemRate', 'salesprice1'].includes(field)) handleRateChange(field, value, index);
               if (['addLess1Percent', 'addLess2Percent', 'addLess3Percent'].includes(field))
                  handleDiscountChange(field, value, index);

               // updatedLineItems[index] = currentItem;

               // ✅ Recalculate totals
               const totals = row.lineItem.reduce(
                  (acc, item) => ({
                     basicAmount: acc.basicAmount + (parseFloat(item.itemRate) || 0) * (parseFloat(item.qty1) || 0),
                     gstAmount: acc.gstAmount + (parseFloat(item.gstAmount) || 0),
                     cgstAmount: acc.cgstAmount + (parseFloat(item.cgstAmount) || 0),
                     sgstAmount: acc.sgstAmount + (parseFloat(item.sgstAmount) || 0),
                     igstAmount: acc.igstAmount + (parseFloat(item.igstAmount) || 0),
                     utgstAmount: acc.utgstAmount + (parseFloat(item.utgstAmount) || 0),
                  }),
                  {
                     basicAmount: 0,
                     gstAmount: 0,
                     cgstAmount: 0,
                     sgstAmount: 0,
                     igstAmount: 0,
                     utgstAmount: 0,
                  },
               );

               // ✅ Calculate Total Taxable Value
               const newTotalTaxableValue = updatedLineItems.reduce(
                  (sum, item) => sum + (parseFloat(item.salesprice1) || 0) * (parseFloat(item.qty1) || 0),
                  0,
               );

               setTotalTaxableValue(newTotalTaxableValue.toFixed(2));

               // ✅ Calculate GST Totals
               const parseValue = (value) => parseFloat(value) || 0;
               const newTotalGSTValues = row.lineItem.reduce(
                  (acc, { gstAmount, cgstAmount, sgstAmount, igstAmount, utgstAmount }) => {
                     acc.gstAmount += parseValue(gstAmount);
                     acc.cgstAmount += parseValue(cgstAmount);
                     acc.sgstAmount += parseValue(sgstAmount);
                     acc.igstAmount += parseValue(igstAmount);
                     acc.utgstAmount += parseValue(utgstAmount);
                     return acc;
                  },
                  {
                     gstAmount: 0,
                     cgstAmount: 0,
                     sgstAmount: 0,
                     igstAmount: 0,
                     utgstAmount: 0,
                  },
               );

               setTotalGSTValues({
                  gstAmount: newTotalGSTValues.gstAmount.toFixed(2),
                  cgstAmount: newTotalGSTValues.cgstAmount.toFixed(2),
                  sgstAmount: newTotalGSTValues.sgstAmount.toFixed(2),
                  igstAmount: newTotalGSTValues.igstAmount.toFixed(2),
                  utgstAmount: newTotalGSTValues.utgstAmount.toFixed(2),
               });

               return {
                  ...prev,
                  lineItem: updatedLineItems,
                  header: {
                     ...prev.header,
                     basicAmount: totals.basicAmount.toFixed(2),
                     netAmount: (totals.basicAmount + totals.gstAmount).toFixed(2),
                     // totalCGST: totals.cgstAmount.toFixed(2),
                     // totalSGST: totals.sgstAmount.toFixed(2),
                     // totalIGST: totals.igstAmount.toFixed(2),
                     // totalUTGST: totals.utgstAmount.toFixed(2),
                  },
               };
            });

            if (field === 'typeOfSupply') {
               handleTypeOfSupplyChange(value, index);
            }

            if (field === 'itemId') {
               handleItemSelection(value, index);
               setLastItemId(Number(value));
               handleStockModal(value, index);
            } else if (field === 'salesprice1' || field === 'qty1' || field === 'qty2' || field === 'qty3') {
               // Handle changes in salePrice or quantities for line items.
               const parsedValue = parseFloat(value);

               // Ensure valid number input
               if (!isNaN(parsedValue)) {
                  setRow((prev) => {
                     const updatedLineItems = prev.lineItem.map((item, i) =>
                        i === index
                           ? {
                              ...item,
                              [field]: parsedValue,
                              itemRate: item.itemRate || 0 * (item.qty1 || 0),
                              amount: (item.salesprice1 || 0) * (item.qty1 || 0), // Calculate amount based on salesprice1 and qty1, // Keep the existing amount if the field is not salesprice1 or qty1
                           }
                           : item,
                     );
                     // Recalculate basicAmount after line item update
                     const newBasicAmount = updatedLineItems.reduce((total, item) => {
                        const lineTotal = (parseFloat(item.salesprice1) || 0) * (parseFloat(item.qty1) || 0); // Use parseFloat and fallback to 0 if invalid
                        return total + lineTotal;
                     }, 0);
                     const additionAmount = parseFloat(prev.header.additionAmount) || 0;
                     const deductionAmount = parseFloat(prev.header.deductionAmount) || 0;
                     const netAmount = newBasicAmount + additionAmount - deductionAmount;

                     return {
                        ...prev,
                        lineItem: updatedLineItems,
                        header: {
                           ...prev.header,
                           basicAmount: Math.floor(newBasicAmount).toString(), // Ensure basicAmount is an integer
                           netAmount: Math.floor(netAmount).toString(),
                        },
                     };
                  });
               }
            } else {
               // Handle other fields (typeOfSupply, etc.)
               setRow((prev) => ({
                  ...prev,
                  lineItem: prev.lineItem.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
               }));
            }
         } else if (section === 'header') {
            if (field === 'transactionAt') {
               setRow((prev) => ({
                  ...prev,
                  header: { ...prev.header, partyDate: value },
               }));
            }
            setRow((prev) => {
               const updatedRow = {
                  ...prev,
                  header: {
                     ...prev.header,
                     [field]: value,
                  },
               };

               // Recalculate netAmount when relevant fields change
               if (field === 'basicAmount' || field === 'additionAmount' || field === 'deductionAmount') {
                  const basicAmount = parseFloat(updatedRow.header.basicAmount) || 0;
                  const additionAmount = parseFloat(updatedRow.header.additionAmount) || 0;
                  const deductionAmount = parseFloat(updatedRow.header.deductionAmount) || 0;
                  updatedRow.header.netAmount = basicAmount + additionAmount - deductionAmount;
               }

               return updatedRow;
            });
         }
      },
      [
         dispatch,
         handleDiscountChange,
         handleItemSelection,
         handleQtyChange,
         handleRateChange,
         handleStockModal,
         handleTypeOfSupplyChange,
         row.lineItem,
         setDaybookId,
      ],
   );

   // Create refs for each input field
   const refs = {
      transactionAt: useRef(null),
      dueDays: useRef(null),
      dueAt: useRef(null),
      companyId: useRef(null),
      accountId: useRef(null),
      additionAmount: useRef(null),
      deductionAmount: useRef(null),
   };

   // Define the focus order
   const focusOrder = [
      'transactionAt',
      'dueDays',
      'dueAt',
      'companyId',
      'accountId',
      'additionAmount',
      'deductionAmount',
   ];

   const handleKeyDown = (e, currentField) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         const currentIndex = focusOrder.indexOf(currentField);
         const nextField = focusOrder[currentIndex + 1];

         if (nextField) {
            const nextRef = refs[nextField];
            if (nextRef?.current) {
               nextRef.current.focus();

               // If it's a select element, open the dropdown
               if (nextRef.current.tagName === 'SELECT') {
                  nextRef.current.click();
               }
            }
         }
      }
   };

   const renderFormControlWithRef = (name, label, props, errors, textColor, type, required) => {
      return renderFormControl(
         name,
         label,
         {
            ...props,
            ref: refs[name],
            onKeyDown: (e) => handleKeyDown(e, name),
         },
         errors,
         textColor,
         type,
         required,
      );
   };

   const renderSelectFormControlWithRef = (name, label, options, props, errors, textColor, required, placeholder) => {
      return renderSelectFormControl(
         name,
         label,
         options,
         {
            ...props,
            ref: refs[name],
            onKeyDown: (e) => handleKeyDown(e, name),
         },
         errors,
         textColor,
         required,
         placeholder,
      );
   };

   // Set initial focus to transaction date when modal opens
   useEffect(() => {
      // Use a slightly longer timeout to ensure modal is fully mounted
      const timer = setTimeout(() => {
         if (refs.transactionAt.current) {
            refs.transactionAt.current.focus();
         }
      }, 300);

      return () => clearTimeout(timer);
   }, [refs.transactionAt]);

   useEffect(() => {
      if (daybookId) {
         setRow((prev) => ({
            ...prev,
            header: {
               ...prev.header,
               daybookId: Number(daybookId),
            },
         }));
      }
   }, [daybookId]);

   const addLineItem = () => {
      setRow((prev) => ({
         ...prev,
         lineItem: [...prev.lineItem, { ...initialLineItem }],
      }));
   };

   const removeLineItem = (index, id) => {
      if (id) {
         setDeleteLineItemId(id);
         setDeleteDialogOpen(true);
      }
      if (row.lineItem?.length > 1) {
         setRow((prev) => ({
            ...prev,
            lineItem: prev.lineItem.filter((_, i) => i !== index),
         }));
      }
   };

   const handleDelete = () => {
      dispatch(deletePurchaseLineItem(deleteLineItemId));
      setDeleteLineItemId(null);
      setDeleteDialogOpen(false);
   };

   const handleSave = (formData) => {
      if (isEditMode && currentRow) {
         setData(data?.map((row) => (row.id === currentRow.id ? { ...currentRow, ...formData } : row)));
      } else if (!isEditMode) {
         setData([...data, { ...formData, id: Date.now() }]);
      }
      onClose();
   };

   const handleStock = useCallback(() => {
      let newData = [];
      let arr = [];
      if (id) {
         // arr.push(rows[rows?.length - 1])
         newData = rows.map((row) => {
            return {
               serialNo: row.serialNo,
               serialNo2: row.serialNo2,
               barcode: row.barcode,
               note1: row.note1 ?? '',
               isOld: row.isOld,
               itemId: lastItemId,
               itemName: itemData.find((a) => a.id === lastItemId)?.name,
            };
         });
      } else {
         newData = rows.map((i) => ({
            serialNo: i.serialNo,
            serialNo2: i.serialNo2,
            barcode: i.barcode,
            note1: i.note1 ?? '',
            isOld: i.isOld,
            itemId: lastItemId,
            itemName: itemData.find((a) => a.id === lastItemId)?.name,
         }));
      }

      setRow((prev) => ({
         ...prev,
         lineItem: prev.lineItem.map((item, idx) => {
            if (currentIndex === idx) {
               const selectedItem = itemData?.find((a) => a.id.toString() === item.itemId.toString());

               const lineItem =
                  selectedItem && selectedItem.salesprice1
                     ? selectedItem.salesprice1
                     : salesInvoice?.lineitems[currentIndex]?.itemRate;

               // const stockData = removeDuplicates(newData, "serialNo")
               // [...item.stock, ...arr.map((i) => ({ note1: i.note1, serialNo: i.serialNo, serialNo2: i.serialNo2, isOld: i.isOld, barcode: i.barcode, itemId: selectedItem.id, itemName: selectedItem.name }))]
               const stockData = id ? newData : [...item.stock, ...newData];
               const stocks = removeDuplicates(stockData, 'serialNo');
               if (stocks?.length > 0) handleQtyChange('qty1', stocks?.length, idx);
               return {
                  ...item,
                  stock: stocks,
                  qty1: stocks?.length,
                  itemRate: lineItem,
               };
            }
            return item;
         }),
      }));

      // setSelectedStocks((prev) => [...prev, ...newData]);
      setRows([{ serialNo: '', serialNo2: '', barcode: '', note1: '', isOld: selectedDaybook?.isOld }]);
      onCloseModal();
   }, [currentIndex, handleQtyChange, id, itemData, lastItemId, onCloseModal, rows, salesInvoice?.lineitems]);

   const removeStock = useCallback(
      (stock) => {
         // setSelectedStocks((prev) => prev.filter((e) => e.serialNo !== id));
         setRow((prev) => ({
            ...prev,
            lineItem: prev?.lineItem?.map((item, idx) => {
               const stockData = item.stock.filter((e) => e.serialNo?.toString() !== stock.serialNo?.toString());
               if (stockData?.length > 0) handleQtyChange('qty1', stockData?.length, idx);

               return { ...item, stock: stockData, qty1: stockData?.length };
            }),
         }));

         // if (id) dispatch(deletePurchaseLineDetails(stock.id));
      },
      [handleQtyChange],
   );

   const validateForm = async () => {
      try {
         await validationSchema.validate(row, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const newErrors = {};
            err.inner.forEach((error) => {
               newErrors[error.path] = error.message;
            });
            setErrors(newErrors);
            return false;
         }
         return false;
      }
   };

   const handleSubmit = async () => {
      const isValid = await validateForm();
      const formatDate = (date) => {
         if (!date) return null;
         const d = new Date(date);
         return d instanceof Date && !isNaN(d) ? d.toISOString() : null;
      };

      if (isValid) {
         const payload = {
            header: {
               ...row.header,
               ...(id && { id: id }),
               basicAmount: Number(totals.netAmount),
               netAmount: Number(finalNetAmount),
               companyId: Number(user?.loginhistory?.companyId),
               accountId: Number(row.header.accountId),
               daybookId: Number(row.header.daybookId),
               additionAmount: row.header.additionAmount ? Number(row.header.additionAmount) : 0,
               deductionAmount: row.header.deductionAmount ? Number(row.header.deductionAmount) : 0,
               dueDays: dueDays ? Number(dueDays) : 0,
               partyNo: row.header.partyNo,
               partyDate: formatDate(row.header.partyDate),
               transactionAt: formatDate(row.header.transactionAt),
               dueAt: formatDate(row.header.dueAt),
               gstAmount: totals.netGstAmount ? totals.netGstAmount : 0,
               warehouseId: getDefaultId(warehouse, 'DEFAULT'),
               taxableAmount: totals.taxableAmount ? totals.taxableAmount : 0,
               billtoStateId: getAccountAddress(account, row.header.accountId)?.billingAddress?.id ?? null,
               shiptoStateId: getAccountAddress(account, row.header.accountId)?.shippingAddress?.id ?? null,
               gstTxnType: getGstTaxType(companyList, account, 'purchase'),
               gstSubTxnType: getGstSubTaxType(companyList, account, 'purchase'),
               assesseType: account?.assesseType ? account?.assesseType : 0,
               distance: account?.distanceKM ? account?.distanceKM : 0,
            },
            lineItem: row.lineItem.map((items) => ({
               ...(items.id && { id: items.id }),
               typeOfSupply: items.typeOfSupply ? items.typeOfSupply : 'Goods',
               itemId: items.itemId ? Number(items.itemId) : 0,
               lineNumber: items.lineNumber ? Number(items.lineNumber) : 1,
               itemRate: items.itemRate ? Number(items.itemRate) : 0,
               amount: items.amount ? Number(items.amount) : 0,
               netAmount: items.netAmount ? Number(items.netAmount) : 0,
               ratePer: items.ratePer ? Number(items.ratePer) : 0,
               uomNoForRate: items.uomNoForRate ? Number(items.uomNoForRate) : 0,
               qty1: items.qty1 ? Number(items.qty1) : 0,
               qty2: items.qty2 ? Number(items.qty2) : 0,
               qty3: items.qty3 ? Number(items.qty3) : 0,
               addLess1Percent: items.addLess1Percent ? Number(items.addLess1Percent) : 0,
               addLess1Amount: items.addLess1Amount ? items.addLess1Amount : 0,
               addLess2Percent: items.addLess2Percent ? Number(items.addLess2Percent) : 0,
               addLess2Amount: items.addLess2Amount ? items.addLess2Amount : 0,
               addLess3Percent: items.addLess3Percent ? Number(items.addLess3Percent) : 0,
               addLess3Amount: items.addLess3Amount ? items.addLess3Amount : 0,
               taxableAmount: items.taxableAmount ? items.taxableAmount : 0,
               netGst: items.netGst ? items.netGst : 0,
               gstPercent: items.gst ? items.gst : 0,
               cgst: totals.taxableAmount,
               sgst: totals.taxableAmount,
               igst: totals.taxableAmount,
               stock: items.stock?.length > 0 ? items.stock : [],
            })),
            purchaseBillTerms: billTerms.map((term) => ({
               ...(term.id && { id: term.id }),
               billTermId: Number(term.billTermId),
               percentage: Number(term.percentage),
               amount: Number(term.amount),
            })),
         };

         if (id) {
            await dispatch(updatePurchaseInvoice(payload));
         } else {
            await dispatch(addPurchaseInvoice(payload));
         }
         dispatch(getDaybook({ companyId: user?.loginhistory?.companyId, type: 5 }));
         await dispatch(
            getPurchaseInvoices({
               companyId: user?.loginhistory?.companyId,
               page: 1,
               limit: 10,
               sortBy: 'id',
               sortOrder: 'desc',
            }),
         );
         localStorage.removeItem('daybook');
         localStorage.removeItem('accountId');
         clearFormData();
         navigate('/admin/purchase/invoice');
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please fill in all required fields correctly.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   useEffect(() => {
      if (row?.header?.accountId) {
         const acc = account?.find((a) => a?.id?.toString() === row?.header?.accountId?.toString());
         const state = acc?.accountaddress?.find((a) => a.name === 'billing')?.stateId;
         const companyState = companyList?.find((c) => c.id?.toString() === user?.loginhistory?.companyId?.toString());
         setIsSameState(state?.toString() === companyState?.stateid?.toString());
      }
   }, [account, companyList, row?.header?.accountId, user?.loginhistory?.companyId]);

   // Function to add a new row
   const addRow = () => {
      setRows([...rows, { serialNo: '', serialNo2: '', barcode: '', note1: '', isOld: selectedDaybook?.isOld }]);
   };

   // Function to remove a row
   const removeRow = (index) => {
      setRows(rows.filter((_, i) => i !== index));
   };

   const handleChange = (e, index) => {
      const { name, value, type, checked } = e.target;
      const newRows = [...rows];
      newRows[index][name] = type === 'checkbox' ? checked : value;
      setRows(newRows);
   };

   useEffect(() => {
      const processAllItems = () => {
         const selectStock = row.lineItem.map((item) => item.stock);
         const flattenedStocks = selectStock.flat(Infinity);
         setSelectedStocks((prevStocks) => {
            if (JSON.stringify(prevStocks) !== JSON.stringify(flattenedStocks)) {
               return flattenedStocks;
            }
            return prevStocks;
         });
      };

      if (row.lineItem && row.lineItem?.length > 0) {
         processAllItems();
      } else {
         setSelectedStocks([]);
      }
   }, [row.lineItem]);

   const nextInputRef = useRef(null);

   const handleAccountSave = async (data) => {
      await dispatch(addAccount(data));
      dispatch(getAccount({ compId: user?.loginhistory?.compId, status: true }));
      const storedAccountData = localStorage.getItem('accountData');
      const parsedAccountData = storedAccountData ? JSON.parse(storedAccountData) : null;
      setSelectedAccount({
         label: parsedAccountData.name,
         value: parsedAccountData.id,
         ...parsedAccountData,
      });

      localStorage.removeItem('accountId');
      onClose();
   };

   useEffect(() => {
      if (row.header.daybookId && user && user?.loginhistory?.companyId) {
         const daybookById = {
            id: Number(row.header.daybookId), // id must be number
            compId: String(user?.loginhistory?.companyId), // compId must be string
         };
         dispatch(getDaybookById(daybookById));
      }
   }, [dispatch, row.header.daybookId, user]);

   useEffect(() => {
      if (selectAccount) {
         setSelectedAccount(selectAccount);
      }
   }, [selectAccount]);

   return (
      <>
         <Card
            flexDirection="column"
            w="100%"
            px="0px"
            overflowX={{ sm: 'scroll', lg: 'hidden' }}
            mt="-150px"
            position="relative"
            minH={{ base: '50vh', lg: '60vh', xl: '90vh' }}
            maxH={{ base: '50vh', lg: '60vh', xl: '90vh' }}
            overflowY={'scroll'}>
            <CardHeader>
               <Heading size="md">{id ? 'Update' : 'Add'} Purchase Invoice</Heading>
            </CardHeader>
            <CardBody>
               <Grid display="flex">
                  <Grid width="55%">
                     <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <GridItem colSpan={1}>
                           <Flex align="center">
                              {renderSelectFormControlWithRef(
                                 'daybookId',
                                 'Daybook',
                                 daybookData.filter((item) => item.type === 11),
                                 {
                                    value: row.header.daybookId,
                                    onChange: (value) => handleInputChange('header', 'daybookId', value),
                                    focusBorderColor: 'blue.500', // Set the border color when focused
                                    _focus: {
                                       boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)', // Optional, add a shadow effect when focused
                                    },
                                 },
                                 errors,
                                 textColor,
                                 true,
                                 'Select Daybook',
                              )}
                              <Button variant="ghost" onClick={() => onOpen()}>
                                 <FaPlus style={{ marginLeft: 4 }} />
                              </Button>
                           </Flex>
                        </GridItem>

                        <GridItem>
                           {renderFormControlWithRef(
                              'docno',
                              'Document Number',
                              {
                                 value: row.header.docno,
                                 onChange: (e) => handleInputChange('header', 'docno', e.target.value),
                                 focusBorderColor: 'blue.500', // Set the border color when focused
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)', // Optional, add a shadow effect when focused
                                 },
                              },
                              errors,
                              textColor,
                              'text',
                              true,
                           )}
                        </GridItem>
                     </Grid>

                     <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        <GridItem colSpan={1}>
                           {renderFormControlWithRef(
                              'transactionAt',
                              'Transaction Date',
                              {
                                 value: row.header.transactionAt,
                                 onChange: (e) => handleInputChange('header', 'transactionAt', e.target.value),
                                 focusBorderColor: 'blue.500', // Set the border color when focused
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)', // Optional, add a shadow effect when focused
                                 },
                              },
                              errors,
                              textColor,
                              'date',
                              true,
                           )}
                        </GridItem>
                        <GridItem>
                           <Flex align="center" width="100%">
                              <GstDropDown
                                 ref={nextInputRef}
                                 handleInputChange={handleInputChange}
                                 targetGlGroup={targetGlGroup}
                                 selectedAccount={selectedAccount}
                                 setSelectedAccount={setSelectedAccount}
                              />
                           </Flex>
                        </GridItem>
                     </Grid>
                  </Grid>
                  {selectedAccount && selectedAccount.gstRegNo && selectedAccount.email ? (
                     <Grid
                        border="1.5px solid"
                        borderColor="gray.200"
                        borderRadius="15px"
                        padding="20px"
                        marginLeft="40px"
                        width="45%"
                        display="flex">
                        <Grid>
                           {selectedAccount.mobile ? (
                              <GridItem display="flex" alignItems="center">
                                 <Image src={phone} alt="editicon" boxSize="15px" mr={2} />
                                 <span>{selectedAccount.mobile}</span>
                              </GridItem>
                           ) : null}

                           {selectedAccount.billingaddressname ? (
                              <Grid justify-items="start" marginTop="20px">
                                 <Box fontWeight="500">Billing Address</Box>
                                 <GridItem display="flex" alignItems="flex-start" paddingTop="10px">
                                    <Image src={map} alt="editicon" boxSize="15px" mr={2} mt={1} />
                                    <span>
                                       {selectedAccount.billingaddressname ? (
                                          <>
                                             {selectedAccount.billingaddressname.substring(
                                                0,
                                                selectedAccount.billingaddressname.indexOf(',') + 13,
                                             )}{' '}
                                             <br />
                                             {selectedAccount.billingaddressname.substring(
                                                selectedAccount.billingaddressname.indexOf(',') + 14,
                                             )}
                                          </>
                                       ) : null}
                                    </span>
                                 </GridItem>
                              </Grid>
                           ) : null}
                        </Grid>
                        <Grid marginLeft="60px">
                           {selectedAccount.email ? (
                              <GridItem display="flex" alignItems="center" height="24px" marginBottom="0px">
                                 <Image src={Email} alt="editicon" boxSize="15px" mr={2} />
                                 <span>{selectedAccount.email}</span>
                              </GridItem>
                           ) : null}

                           {selectedAccount.address1 ? (
                              <Grid justify-items="start" marginTop="20px">
                                 <Box fontWeight="500">Shipping Address</Box>
                                 <GridItem display="flex" paddingTop="10px">
                                    <Image src={map} alt="editicon" boxSize="15px" mr={2} />
                                    <span>
                                       {selectedAccount.address1 ? (
                                          <>
                                             {selectedAccount.address1.substring(
                                                0,
                                                selectedAccount.address1.indexOf(',') + 13,
                                             )}{' '}
                                             <br />
                                             {selectedAccount.address1.substring(
                                                selectedAccount.address1.indexOf(',') + 14,
                                             )}
                                          </>
                                       ) : null}
                                    </span>
                                 </GridItem>
                              </Grid>
                           ) : null}
                        </Grid>
                     </Grid>
                  ) : null}
               </Grid>
               <Grid templateColumns="repeat(4, 1fr)" gap={6}>
                  <GridItem>
                     <Flex align="center">
                        {renderFormControlWithRef(
                           'partyNo',
                           'Party Number',
                           {
                              value: row.header.partyNo,
                              onChange: (e) => handleInputChange('header', 'partyNo', e.target.value),
                              focusBorderColor: 'blue.500', // Set the border color when focused
                              _focus: {
                                 boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)', // Optional, add a shadow effect when focused
                              },
                           },
                           errors,
                           textColor,
                           'text',
                           true,
                        )}
                        {/* <Button variant="ghost" onClick={() => onPartyOpen()}>
                  <FaPlus style={{ marginLeft: 4, marginTop: -24 }} />
                </Button> */}
                     </Flex>
                  </GridItem>

                  <GridItem colSpan={1}>
                     {renderFormControlWithRef(
                        'partyDate',
                        'Party Date',
                        {
                           value: row.header.partyDate,
                           onChange: (e) => handleInputChange('header', 'partyDate', e.target.value),
                           focusBorderColor: 'blue.500', // Set the border color when focused
                           _focus: {
                              boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)', // Optional, add a shadow effect when focused
                           },
                        },
                        errors,
                        textColor,
                        'date',
                        true,
                     )}
                  </GridItem>
               </Grid>

               <Grid templateColumns="repeat(4, 1fr)" gap={6}></Grid>
               <GridItem colSpan={4}>
                  <LineItemsTable
                     key={row.lineItem?.length}
                     lineItems={row.lineItem}
                     textColor={textColor}
                     itemData={itemData}
                     daybookData={daybookData}
                     showGSTColumn={showGSTColumn}
                     insertItem={addLineItem}
                     removeItem={removeLineItem}
                     handleInputChange={handleInputChange}
                     updateTotalAmount={updateTotalAmount}
                     setSelectedItemParent={setSelectedItemParent}
                     refs={refs}
                     nextInputRef={nextInputRef}
                     errors={errors}
                     lastSaleDaybook={lastPurchaseDaybook}
                     totals={totals}
                     companyData={companyList}
                     handleStockModalOnClick={handleStockModalOnClick}
                     setLastItemId={setLastItemId}
                  />
               </GridItem>

               <Grid templateColumns="repeat(4, 1fr)" gap="6">
                  <GridItem colSpan={2}>
                     <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={4}>
                        <GridItem>
                           {renderFormControlWithRef(
                              'dueDays',
                              'Due Days',
                              {
                                 value: dueDays,
                                 onChange: (e) => {
                                    const newDueDays = e.target.value;
                                    setDueDays(Number(newDueDays));
                                    handleInputChange('header', 'dueDays', newDueDays);
                                 },
                                 focusBorderColor: 'blue.500', // Set the border color when focused
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)', // Optional, add a shadow effect when focused
                                 },
                              },
                              errors,
                              textColor,
                              'number',
                              true,
                           )}
                        </GridItem>
                        <GridItem>
                           {renderFormControlWithRef(
                              'dueAt',
                              'Due At',
                              {
                                 value: dueAt,
                                 onChange: (e) => {
                                    const newDueAt = e.target.value;
                                    setDueAt(newDueAt);
                                    handleInputChange('header', 'dueAt', newDueAt);
                                 },
                                 focusBorderColor: 'blue.500', // Set the border color when focused
                                 _focus: {
                                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)', // Optional, add a shadow effect when focused
                                 },
                              },
                              errors,
                              textColor,
                              'date',
                              true,
                           )}
                        </GridItem>
                     </Grid>
                  </GridItem>
                  <GridItem colSpan={2}>
                     <Flex justifyContent="flex-end">
                        {/* <GridItem colSpan={8}> */}
                        <BillTerms
                           billTermData={row.header.daybookId > 0 ? billTermData : []}
                           billTerms={row.header.daybookId > 0 ? billTerms : []}
                           handleBillTermChange={handleBillTermChange}
                           errors={errors}
                           textColor={textColor}
                           basicAmount={totals.netAmount}
                           netAmount={amounts.netAmount}
                        />
                     </Flex>
                     {/* </GridItem> */}
                  </GridItem>
               </Grid>
               <Grid templateColumns="repeat(4, 1fr)" gap="6">
                  <GridItem colSpan={2}>
                     <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={4}>
                        {selectedStocks?.length > 0 ? (
                           <GridItem>
                              <TableContainer>
                                 <Table variant="simple" border="1px solid #E2E8F0">
                                    <Thead>
                                       <Tr borderBottom="1px solid #E2E8F0">
                                          <Th borderBottom="1px solid #E2E8F0">Item</Th>
                                          <Th borderBottom="1px solid #E2E8F0">Series No 1</Th>
                                          <Th borderBottom="1px solid #E2E8F0">Series No 2</Th>
                                          <Th borderBottom="1px solid #E2E8F0">Barcode</Th>
                                          <Th borderBottom="1px solid #E2E8F0">Note</Th>
                                          <Th borderBottom="1px solid #E2E8F0">Action</Th>
                                       </Tr>
                                    </Thead>
                                    <Tbody>
                                       {selectedStocks &&
                                          removeDuplicates(selectedStocks, 'serialNo')?.map((item, index) => {
                                             return (
                                                <Tr key={index} borderBottom="1px solid #E2E8F0">
                                                   <Td borderBottom="1px solid #E2E8F0">{item?.itemName}</Td>
                                                   <Td borderBottom="1px solid #E2E8F0">{item?.serialNo}</Td>
                                                   <Td borderBottom="1px solid #E2E8F0">{item?.serialNo2}</Td>
                                                   <Td borderBottom="1px solid #E2E8F0">{item?.barcode}</Td>
                                                   <Td borderBottom="1px solid #E2E8F0">{item?.note1}</Td>
                                                   {/* <Td
                                    borderBottom="1px solid #E2E8F0"
                                    isNumeric
                                  >
                                    {item.sum}
                                  </Td> */}
                                                   <Td borderBottom="1px solid #E2E8F0">
                                                      <IconButton
                                                         colorScheme="red"
                                                         onClick={() => removeStock(item)}
                                                         icon={<FaTrash />}
                                                      />
                                                   </Td>
                                                </Tr>
                                             );
                                          })}
                                    </Tbody>
                                 </Table>
                              </TableContainer>
                           </GridItem>
                        ) : (
                           <>
                              <GridItem></GridItem>
                              <GridItem></GridItem>
                           </>
                        )}
                     </Grid>
                  </GridItem>

                  <GridItem colSpan={2}>
                     <Flex justifyContent="flex-end">
                        {/* <GridItem colSpan={8}> */}
                        {/* {showGSTColumn && ( */}
                        <TaxableValue
                           billTermData={billTermData}
                           billTerms={billTerms}
                           handleBillTermChange={handleBillTermChange}
                           errors={errors}
                           textColor={textColor}
                           basicAmount={row.header.basicAmount}
                           totalTaxableValue={totalTaxableValue}
                           totalGSTValues={totalGSTValues}
                           netAmount={amounts.netAmount}
                           isSameState={isSameState}
                           totals={totals}
                           finalNetAmount={finalNetAmount}
                        />
                        {/* )} */}
                     </Flex>
                     {/* </GridItem> */}
                  </GridItem>
               </Grid>
            </CardBody>
            <CardFooter justifyContent="flex-end">
               <Button
                  onClick={handleSubmit}
                  variant="darkBrand"
                  color="white"
                  fontSize="sm"
                  fontWeight="500"
                  borderRadius="full"
                  px="24px"
                  py="5px"
                  _active={{ bg: '#06b' }}
                  mr={5}>
                  {id ? 'Update' : 'Save'}
               </Button>
               <Button
                  variant="ghost"
                  onClick={() => {
                     clearFormData();
                     navigate('/admin/purchase/invoice');
                     localStorage.removeItem('daybook');
                     localStorage.removeItem('accountId');
                  }}>
                  Cancel
               </Button>
            </CardFooter>
         </Card>
         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="Delete Line Item"
         />

         {isOpen ? (
            <DaybookForm
               isOpen={isOpen}
               onClose={onClose}
               isEditMode={isEditMode}
               currentRow={currentRow}
               onSave={handleSave}
            />
         ) : null}

         {isAccountOpen ? (
            <AddEditForm
               isOpen={isAccountOpen}
               onClose={onAccountClose}
               isEditMode={isEditMode}
               currentRow={currentRow}
               initialData={currentRow}
               onSave={handleAccountSave}
               parentGlGroup={targetGlGroup?.id}
            />
         ) : null}

         <Modal isOpen={isOpenModal} onClose={onCloseModal} size="6xl" isCentered>
            <ModalOverlay />
            <ModalContent
               bg={useColorModeValue('white', '#2d3748')}
               color={useColorModeValue('gray.800', 'white')}
               borderRadius="20px"
               p={5}
               boxShadow="lg">
               <ModalHeader fontSize="20px" fontWeight="600" mb={4}>
                  Add Serial Number
               </ModalHeader>
               <ModalCloseButton />
               <ModalBody>
                  <Grid gap={2}>
                     {rows?.map((row, index) => (
                        <Grid
                           display="flex"
                           gap={5}
                           width="100%"
                           justifyContent="flex-start"
                           alignItems="center"
                           key={row.id}
                           mt={index > 0 ? 3 : 0}>
                           <GridItem>
                              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                 <FormLabel style={{ textWrap: 'nowrap' }}>{`Serial No 1`}</FormLabel>
                                 <Input
                                    type="text"
                                    name="serialNo"
                                    value={row.serialNo}
                                    onChange={(e) => handleChange(e, index)}
                                    onKeyDown={(e) => {
                                       if (e.key === 'Enter') addRow();
                                    }}
                                    autoFocus={index === rows?.length - 1}
                                 />
                              </FormControl>
                           </GridItem>
                           <GridItem>
                              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                 <FormLabel style={{ textWrap: 'nowrap' }}>{`Serial No 2`}</FormLabel>
                                 <Input
                                    type="text"
                                    name="serialNo2"
                                    value={row.serialNo2}
                                    onChange={(e) => handleChange(e, index)}
                                    onKeyDown={(e) => {
                                       if (e.key === 'Enter') addRow();
                                    }}
                                 // autoFocus={index === rows?.length - 1}
                                 />
                              </FormControl>
                           </GridItem>
                           <GridItem>
                              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                 <FormLabel style={{ textWrap: 'nowrap' }}>Barcode</FormLabel>
                                 <Input
                                    type="text"
                                    name="barcode"
                                    value={row.barcode}
                                    onChange={(e) => handleChange(e, index)}
                                    onKeyDown={(e) => {
                                       if (e.key === 'Enter') addRow();
                                    }}
                                    disabled
                                 // autoFocus={index === rows?.length - 1}
                                 />
                              </FormControl>
                           </GridItem>
                           <GridItem>
                              <Flex justifyContent="start" alignItems="center" gap={3}>
                                 <FormControl display="flex" alignItems="center" justifyContent="space-between">
                                    <FormLabel style={{ textWrap: 'nowrap' }}>{`Note`}</FormLabel>
                                    <Input
                                       type="text"
                                       name="note1"
                                       value={row.note1}
                                       onChange={(e) => {
                                          handleChange(e, index);
                                       }}
                                       onKeyDown={(e) => {
                                          if (e.key === 'Enter') addRow();
                                       }}
                                    />
                                 </FormControl>
                                 <FormControl display="flex" alignItems="center">
                                    <FormLabel htmlFor={`idOld-${index}`} style={{ textWrap: 'nowrap' }}>
                                       Is Old
                                    </FormLabel>
                                    <Switch
                                       name="isOld"
                                       id={`idOld-${index}`}
                                       isChecked={row.isOld}
                                       defaultChecked={selectedDaybook?.isOld}
                                       onChange={(e) => handleChange(e, index)}
                                    />
                                 </FormControl>
                                 {rows?.length > 1 && (
                                    <Button colorScheme="red" size="sm" onClick={() => removeRow(index)}>
                                       -
                                    </Button>
                                 )}
                                 {index === rows.length - 1 && (
                                    <Button colorScheme="green" size="sm" onClick={addRow}>
                                       +
                                    </Button>
                                 )}
                              </Flex>
                           </GridItem>
                        </Grid>
                     ))}
                  </Grid>
               </ModalBody>
               <ModalFooter justifyContent="flex-end" alignItems="center">
                  <Flex>
                     <Button colorScheme="blue" ml={3} width="100%" onClick={() => handleStock()}>
                        Save
                     </Button>
                     <Button colorScheme="red" onClick={() => onCloseModal()} ml={3} width="100%">
                        Cancel
                     </Button>
                  </Flex>
               </ModalFooter>
            </ModalContent>
         </Modal>
      </>
   );
};

export default PurchaseInvoiceMasterForm;
